import { useState } from "react";
import { KeyRound, Plug, RefreshCw } from "lucide-react";
import { Link } from "react-router";
import { useDataProvider, useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BrandMarkGroup, INTEGRATION_BRANDS } from "./brandIcons";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import { callOpenAiChat } from "../providers/supabase/aiApi";
import { describeAnthropicError } from "../ai/providerErrors";
import { DEFAULT_OPENAI_MODEL, modelOptionsForProvider } from "../ai/models";
import { getResolvedWorkspace } from "../providers/supabase/workspaceConfig";

/**
 * Integrations hub - one place to see and control everything Obvious connects
 * to. Config fields save per tenant now; OAuth-based data syncs activate at
 * deployment (the connector schema is provider-agnostic, so each integration
 * is additive).
 */

interface IntegrationDef {
  key: string;
  name: string;
  category: "Analytics" | "Advertising" | "Email" | "Forms" | "AI";
  description: string;
  fields: {
    key: string;
    label: string;
    placeholder: string;
    /** Renders a select instead of a text input. */
    options?: { id: string; label: string }[];
    secret?: boolean;
    /** Defaults to true - set false for a field the badge shouldn't require. */
    required?: boolean;
  }[];
  /** What flips it from configured to live. */
  activation: string;
}

const INTEGRATIONS: IntegrationDef[] = [
  {
    key: "ga4",
    name: "Google Analytics 4",
    category: "Analytics",
    description:
      "Site traffic and conversions joined against Obvious's attribution - sessions on one side, pipeline on the other.",
    fields: [{ key: "propertyId", label: "GA4 property ID", placeholder: "e.g. 3213599XX" }],
    activation: "OAuth connect at deployment; nightly sync into reports.",
  },
  {
    key: "gsc",
    name: "Google Search Console",
    category: "Analytics",
    description: "Queries, clicks, and positions for the pages your forms live on.",
    fields: [{ key: "siteUrl", label: "Property URL", placeholder: "https://client.com" }],
    activation: "OAuth connect at deployment.",
  },
  {
    key: "googleAds",
    name: "Google Ads",
    category: "Advertising",
    description:
      "Spend and campaign performance against attributed closed-won: true cost per opportunity and ROAS per channel.",
    fields: [{ key: "customerId", label: "Customer ID", placeholder: "123-456-7890" }],
    activation: "Developer-token app review, then OAuth at deployment.",
  },
  {
    key: "linkedinAds",
    name: "LinkedIn Ads",
    category: "Advertising",
    description: "Campaign spend and lead-gen performance joined to pipeline.",
    fields: [{ key: "accountId", label: "Ad account ID", placeholder: "e.g. 508123456" }],
    activation: "LinkedIn Marketing API partner approval, then OAuth. Applied for early - lands last.",
  },
  {
    key: "emailSync",
    name: "Email capture & sending (Google Workspace / Microsoft 365)",
    category: "Email",
    description:
      "Every email reaches the timeline via BCC or forward - no inbox connection required. Connect your own account too, to send from inside Obvious with a real Sent folder.",
    fields: [],
    activation:
      "Your workspace's BCC/forward address appears here once deployed - live from day one, no setup needed. Connect your own inbox for sending under Settings → Profile.",
  },
  {
    key: "ai",
    name: "Bring your own AI",
    category: "AI",
    description:
      "Plug in your own Claude or GPT key and the Ask AI panel (bottom right, anywhere in Obvious) answers questions with your CRM data in hand.",
    fields: [
      {
        key: "provider",
        label: "Provider",
        placeholder: "anthropic",
        options: [
          { id: "anthropic", label: "Anthropic (Claude)" },
          { id: "openai", label: "OpenAI (GPT)" },
        ],
      },
      { key: "apiKey", label: "API key", placeholder: "sk-ant-… or sk-…", secret: true },
      {
        key: "model",
        label: "Model (optional)",
        placeholder: "",
        required: false,
        // The real option list is computed at render time from the chosen
        // provider (modelOptionsForProvider) - see getFieldOptions below.
      },
    ],
    activation:
      "Anthropic keys work immediately, straight from your browser. OpenAI keys route through the Obvious server proxy at deployment. Keys stay in your tenant.",
  },
  {
    key: "turnstile",
    name: "Cloudflare Turnstile",
    category: "Forms",
    description: "Invisible CAPTCHA on your forms. Free at any volume.",
    fields: [{ key: "siteKey", label: "Site key", placeholder: "0x4AAA..." }],
    activation: "Secret key is set server-side per tenant at deployment.",
  },
];

const CATEGORY_ORDER = ["AI", "Analytics", "Advertising", "Email", "Forms"] as const;

export const IntegrationsPage = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const integrations = config.integrations ?? {};

  // Most integrations aren't ready for every tenant yet (e.g. the
  // Nylas-backed email/calendar connect needs the paid Nylas tier we
  // haven't bought) - Bring your own AI has no such dependency, so it's
  // the one category that stays up regardless of this flag.
  const integrationsVisible = config.moduleFlags?.integrations !== false;
  const visibleCategories = integrationsVisible ? CATEGORY_ORDER : (["AI"] as const);

  // The BCC/forward capture address is real and live (decisions #46-49) -
  // computed from the resolved workspace rather than left as placeholder
  // copy. (The Calendly card went with its unsigned webhook - review #8;
  // booking is the native scheduler on each user's profile.)
  const workspaceSlug = getResolvedWorkspace()?.slug;
  const captureAddress = workspaceSlug ? `log-${workspaceSlug}@mail.obviouscrm.com` : null;

  const fieldValue = (intKey: string, fieldKey: string) =>
    intKey === "turnstile" && fieldKey === "siteKey"
      ? (config.turnstileSiteKey ?? "")
      : (integrations[intKey]?.fields?.[fieldKey] ?? "");

  // Local state updates instantly (every keystroke); the save round-trip to
  // the tenant's own configuration row only fires on blur/selection, so
  // typing doesn't spam the network. Without this, fields here only ever
  // lived in the browser's local store - never actually reaching the
  // database despite dataProvider.updateConfiguration existing for exactly
  // this.
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify(
        "Couldn't save that - you may need admin access to update integrations.",
        { type: "error" },
      );
    }
  };

  const setField = (
    intKey: string,
    fieldKey: string,
    value: string,
    opts: { persistNow?: boolean } = {},
  ) => {
    const next: ConfigurationContextValue =
      intKey === "turnstile" && fieldKey === "siteKey"
        ? { ...config, turnstileSiteKey: value }
        : {
            ...config,
            integrations: {
              ...integrations,
              [intKey]: {
                ...integrations[intKey],
                fields: { ...integrations[intKey]?.fields, [fieldKey]: value },
              },
            },
          };
    updateConfig(next);
    if (opts.persistNow) void persist(next);
  };

  // The Model field's real options depend on which provider is picked -
  // Anthropic and OpenAI model IDs aren't interchangeable - so it can't be
  // a static list like every other select field here.
  const getFieldOptions = (def: IntegrationDef, f: IntegrationDef["fields"][number]) => {
    if (def.key === "ai" && f.key === "model") {
      return modelOptionsForProvider(fieldValue("ai", "provider") || "anthropic");
    }
    return f.options;
  };

  // A select field always has an effective value (it falls back to its
  // first option in the UI below), so it's never the reason a card reads
  // "not connected" - only a blank required text field is.
  const effectiveValue = (def: IntegrationDef, f: IntegrationDef["fields"][number]) =>
    fieldValue(def.key, f.key) || getFieldOptions(def, f)?.[0]?.id || "";

  const isConfigured = (def: IntegrationDef) =>
    def.fields.length > 0 &&
    def.fields
      .filter((f) => f.required !== false)
      .every((f) => effectiveValue(def, f).trim() !== "");

  const [testState, setTestState] = useState<{
    status: "idle" | "testing" | "ok" | "error";
    message?: string;
  }>({ status: "idle" });

  const testAiConnection = async () => {
    const provider = fieldValue("ai", "provider") || "anthropic";
    const apiKey = fieldValue("ai", "apiKey").trim();
    const model = fieldValue("ai", "model").trim();
    if (!apiKey) {
      setTestState({ status: "error", message: "Add an API key first." });
      return;
    }
    setTestState({ status: "testing" });
    try {
      if (provider === "openai") {
        await callOpenAiChat({
          apiKey,
          model: model || DEFAULT_OPENAI_MODEL,
          messages: [{ role: "user", content: "ping" }],
        });
      } else {
        const res = await fetch("https://api.anthropic.com/v1/models", {
          headers: {
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "anthropic-dangerous-direct-browser-access": "true",
          },
        });
        if (!res.ok) throw new Error(describeAnthropicError(res.status));
      }
      setTestState({ status: "ok", message: "Key verified - Ask AI is ready to use." });
    } catch (e) {
      setTestState({
        status: "error",
        message: e instanceof Error ? e.message : "Couldn't verify that key.",
      });
    }
  };

  const renderActivation = (def: IntegrationDef) => {
    if (def.key === "emailSync" && captureAddress) {
      return (
        <>
          Forward or BCC any message to{" "}
          <span className="font-mono break-all">{captureAddress}</span> to log
          it - live today, no setup needed. Known contacts log automatically;
          anyone else waits in{" "}
          <Link to="/settings/email-capture" className="underline hover:no-underline">
            Settings → Email capture
          </Link>{" "}
          for a human to confirm. Want to send AS your own inbox too? Connect
          it under{" "}
          <Link to="/profile" className="underline hover:no-underline">
            Settings → Profile
          </Link>
          .
        </>
      );
    }
    return def.activation;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold">Integrations</h1>
        <p className="text-sm text-muted-foreground">
          Everything Obvious connects to, in one place. Configuration saves now;
          data syncs switch on with your deployment.
        </p>
      </div>

      {integrationsVisible ? (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2.5">How connecting works</p>
            <div className="grid md:grid-cols-3 gap-4 text-xs text-muted-foreground">
              <div className="flex gap-2">
                <Plug className="w-4 h-4 shrink-0 mt-0.5 text-[#2e6ba9]" />
                <p>
                  <span className="text-foreground font-medium">1. Connect.</span>{" "}
                  Click Connect, sign in with the provider, and approve the
                  scopes. OAuth only - Obvious never sees your password.
                </p>
              </div>
              <div className="flex gap-2">
                <KeyRound className="w-4 h-4 shrink-0 mt-0.5 text-[#2e6ba9]" />
                <p>
                  <span className="text-foreground font-medium">2. Stored per workspace.</span>{" "}
                  The grant is encrypted and scoped to this workspace alone.
                  Disconnect any time - the token is revoked with the provider,
                  not just deleted.
                </p>
              </div>
              <div className="flex gap-2">
                <RefreshCw className="w-4 h-4 shrink-0 mt-0.5 text-[#2e6ba9]" />
                <p>
                  <span className="text-foreground font-medium">3. Synced on a schedule.</span>{" "}
                  Analytics and ad data refresh nightly into Reports; schedulers
                  and inboxes stream in as events. Each card shows its sync
                  health once live.
                </p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground/80 border-t border-border pt-2 mt-3">
              The connect flow activates with your deployed workspace. Until
              then, the fields below save your account identifiers so each
              integration lights up the moment OAuth lands.{" "}
              <Link to="/help/integrations" className="underline hover:no-underline">
                How integrations work
              </Link>
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              Most integrations aren't turned on for this workspace yet - check
              back soon. Bring your own AI is available below.
            </p>
          </CardContent>
        </Card>
      )}

      {visibleCategories.map((cat) => (
        <div key={cat} className="flex flex-col gap-2">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">
            {cat}
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {INTEGRATIONS.filter((d) => d.category === cat).map((def) => (
              <Card key={def.key}>
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2.5">
                    <BrandMarkGroup defs={INTEGRATION_BRANDS[def.key] ?? []} />
                    <p className="font-medium text-sm flex-1">{def.name}</p>
                    {def.fields.length > 0 ? (
                      <span
                        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          isConfigured(def)
                            ? "bg-[#8fb586] text-white"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {isConfigured(def) ? "Configured" : "Not connected"}
                      </span>
                    ) : (
                      // Nothing here to "configure" - this endpoint/address
                      // already accepts traffic regardless of any per-tenant
                      // field, so a Not-connected badge would just be wrong.
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#8fb586] text-white">
                        Live
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{def.description}</p>
                  {def.fields.map((f) => {
                    const options = getFieldOptions(def, f);
                    return (
                      <div key={f.key}>
                        <p className="text-xs text-muted-foreground mb-1">{f.label}</p>
                        {options ? (
                          <Select
                            value={fieldValue(def.key, f.key) || options[0]!.id}
                            onValueChange={(v) =>
                              setField(def.key, f.key, v, { persistNow: true })
                            }
                          >
                            <SelectTrigger className="h-8 text-sm w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {options.map((o) => (
                                <SelectItem key={o.id} value={o.id}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            className="h-8 text-sm"
                            type={f.secret ? "password" : "text"}
                            placeholder={f.placeholder}
                            value={fieldValue(def.key, f.key)}
                            onChange={(e) => setField(def.key, f.key, e.target.value)}
                            onBlur={() => void persist(config)}
                          />
                        )}
                      </div>
                    );
                  })}
                  {def.key === "ai" && (
                    <div className="flex flex-col gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-fit"
                        disabled={testState.status === "testing"}
                        onClick={() => void testAiConnection()}
                      >
                        {testState.status === "testing" ? "Testing…" : "Test connection"}
                      </Button>
                      {testState.status === "ok" && (
                        <p className="text-xs text-[#6a8f6b]">{testState.message}</p>
                      )}
                      {testState.status === "error" && (
                        <p className="text-xs text-[#c25d5d]">{testState.message}</p>
                      )}
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground/80 border-t border-border pt-2">
                    {renderActivation(def)}
                  </p>
                  {(def.category === "Analytics" ||
                    def.category === "Advertising") && (
                    <Link
                      to="/reports/web"
                      className="text-xs underline hover:no-underline"
                    >
                      View the data → Reports → Web & Ads
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

IntegrationsPage.path = "/integrations";
